/* eslint-disable react-hooks/exhaustive-deps */
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import datApi from '../services/dat';
import CopyableText from '../components/CopyableText';
import {
  Button,
  Col,
  Container,
  Form,
  Modal,
  Pagination,
  Row,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { VIOLATION_LIST } from '../constants/violation.constant';
import { PROVIDERS } from '../constants/provider.constant';
import { checkDATConditions, convertHoursToHM } from '../utils';
import { debounce } from 'lodash';
import AdminLogoutButton from '../components/AdminLogoutButton';
import SessionReportExportModal from '../components/SessionReportExportModal';

const CheckedHeaderComponent = ({ onOpenModal, allChecked }) => (
  <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
    <span className='me-2'>Đã kiểm tra</span>
    <Button
      size='sm'
      variant={allChecked ? 'outline-warning' : 'outline-primary'}
      onClick={onOpenModal}
      style={{ whiteSpace: 'nowrap' }}
    >
      {allChecked ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
    </Button>
  </div>
);

function AdminPage() {
  const params = new URLSearchParams(window.location.search);
  const [name, setName] = useState(params.get('name') || '');
  const [checked, setChecked] = useState(true);
  const [drivingClass, setDrivingClass] = useState(params.get('class') || '');
  const gridRef = useRef(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [provider, setProvider] = useState('');
  const [result, setResult] = useState({});
  const [violation, setViolation] = useState('');
  const [rowData, setRowData] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [showCheckAllModal, setShowCheckAllModal] = useState(false);
  const [checkAllAction, setCheckAllAction] = useState('check'); // 'check' hoặc 'uncheck'
  const [showReportModal, setShowReportModal] = useState(false);

  const allChecked = useMemo(() => {
    if (rowData.length === 0) return false;
    return rowData.every(row => row.checked === true);
  }, [rowData]);

  const colDefs = useMemo(() => [
    {
      headerName: 'STT',
      valueGetter: (params) => (params.node ? params.node.rowIndex + 1 : ''),
      width: 60,
      pinned: 'left', // Cố định cột STT bên trái
    },
    {
      headerName: 'Tra cứu',
      width: 210,
      pinned: 'left',
      cellRenderer: (params) => {
        return (
          <div>
            <Button
              onClick={() => {
                const url = `/admin/session?name=${params.data.trainee_ma_dk}`;
                window.open(url, '_blank');
              }}
            >
              Phiên học
            </Button>
            <Button
              className='ms-2'
              onClick={() => {
                const url = `/admin/session/student?name=${params.data.trainee_ma_dk}`;
                window.open(url, '_blank');
              }}
            >
              Học viên
            </Button>
          </div>
        );
      },
    },
    {
      field: 'source',
      headerName: 'Nguồn',
      flex: 1,
      minWidth: 90,
      valueGetter: (params) => {
        if (!params.data.source) return 'Excel';
        return params.data.source;
      },
    },
    {
      field: 'excel_sync',
      headerName: 'Đồng bộ Excel',
      flex: 1,
      minWidth: 130,
      editable: true,
      valueGetter: (params) => {
        return params.data?.excel_sync === true
          ? true
          : params.data?.excel_sync === false
          ? false
          : null;
      },
      cellRenderer: 'agCheckboxCellRenderer',
    },
    {
      field: 'checked',
      headerName: 'Đã kiểm tra',
      flex: 1,
      minWidth: 250,
      editable: true,
      headerComponent: (props) => (
        <CheckedHeaderComponent
          allChecked={allChecked}
          onOpenModal={() => {
            if (rowData.length > 20) {
              toast('Chỉ có thể chọn/bỏ chọn tất cả tối đa 20 phiên. Vui lòng lọc dữ liệu trước.', { type: 'warning' });
              return;
            }
            setCheckAllAction(allChecked ? 'uncheck' : 'check');
            setShowCheckAllModal(true);
          }}
        />
      ),
      valueGetter: (params) => {
        return params.data?.checked === true
          ? true
          : params.data?.checked === false
          ? false
          : null;
      },
      cellRenderer: 'agCheckboxCellRenderer',
    },
    {
      field: 'violation',
      headerName: 'Vi phạm',
      editable: true,
      flex: 1,
      minWidth: 500,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: VIOLATION_LIST,
      },
    },
    {
      field: 'session_id',
      headerName: 'Mã phiên',
      flex: 1,
      minWidth: 360,
      cellRenderer: (params) => {
        if (!params.value) return '';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {params.value}
            </span>
            <Button
              size='sm'
              variant='outline-secondary'
              title='Copy mã phiên'
              onClick={() => {
                navigator.clipboard
                  .writeText(params.value)
                  .then(() => toast(`Đã copy mã phiên ${params.value}`, { type: 'success' }))
                  .catch(() => toast('Copy thất bại', { type: 'error' }));
              }}
              style={{ whiteSpace: 'nowrap', padding: '2px 6px', lineHeight: 1 }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <rect x='9' y='9' width='13' height='13' rx='2' ry='2'></rect>
                <path d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1'></path>
              </svg>
            </Button>
          </div>
        );
      },
    },
    {
      field: 'start_time',
      headerName: 'Thời gian bắt đầu',
      cellRenderer: (params) => {
        return new Date(params.value).toLocaleString('en-GB', {
          timeZone: 'Asia/Ho_Chi_Minh',
        });
      },
    },
    {
      field: 'end_time',
      headerName: 'Thời gian kết thúc',
      cellRenderer: (params) => {
        return new Date(params.value).toLocaleString('en-GB', {
          timeZone: 'Asia/Ho_Chi_Minh',
        });
      },
    },
    { field: 'trainee_name', headerName: 'Tên học viên', cellRenderer: (p) => <CopyableText value={p.value} /> },
    { field: 'trainee_ma_dk', headerName: 'Mã học viên', cellRenderer: (p) => <CopyableText value={p.value} /> },
    {
      field: 'distance',
      headerName: 'Quãng đường',
      valueFormatter: (params) =>
        params.value ? Math.floor(params.value * 100) / 100 : 0,
    },
    {
      field: 'duration',
      headerName: 'Thời gian',
      valueFormatter: (params) =>
        params.value ? Math.floor(params.value * 100) / 100 : 0,
    },
    {
      field: 'auto_distance',
      headerName: 'Quãng đường TĐ',
      valueFormatter: (params) =>
        params.value ? Math.floor(params.value * 100) / 100 : 0,
    },
    {
      field: 'auto_duration',
      headerName: 'Thời gian TĐ',
      valueFormatter: (params) =>
        params.value ? Math.floor(params.value * 100) / 100 : 0,
    },
    {
      field: 'night_distance',
      headerName: 'Quãng đường đêm',
      valueFormatter: (params) =>
        params.value ? Math.floor(params.value * 100) / 100 : 0,
    },
    {
      field: 'night_duration',
      headerName: 'Thời gian đêm',
      valueFormatter: (params) =>
        params.value ? Math.floor(params.value * 100) / 100 : 0,
    },
    {
      field: 'night_duration',
      headerName: 'Thời gian đêm đúng hạng',
      cellRenderer: (params) => {
        let val = 0;

        if (
          params.data.ten_khoa_hoc?.includes('B1') ||
          params.data.ten_khoa_hoc?.includes('BTĐ')
        ) {
          val = ['B1', 'B.01', 'B11'].includes(params.data.vehicle_hang)
            ? params.data.night_duration
              ? params.data.night_duration
              : 0
            : 0;
        } else if (
          params.data.ten_khoa_hoc?.includes('B2') ||
          params.data.ten_khoa_hoc?.includes('BSS')
        ) {
          val = ['B2', 'B'].includes(params.data.vehicle_hang)
            ? params.data.night_duration
              ? params.data.night_duration
              : 0
            : 0;
        } else if (params.data.ten_khoa_hoc?.includes('C1')) {
          console.log(params.data.vehicle_hang);
          val = ['C1', 'C'].includes(params.data.vehicle_hang)
            ? params.data.night_duration
              ? params.data.night_duration
              : 0
            : 0;
        }
        return val;
      },
      valueFormatter: (params) =>
        params.value ? Math.floor(params.value * 100) / 100 : 0,
    },
    {
      field: 'manual_night_duration',
      headerName: 'TG đêm thủ công',
      editable: true,
      valueFormatter: (params) =>
        params.value ? Math.floor(params.value * 100) / 100 : 0,
    },
    {
      field: 'distance_dung_hang',
      headerName: 'Quãng đường đúng hạng',
      valueFormatter: (params) =>
        params.value ? Math.floor(params.value * 100) / 100 : 0,
    },
    {
      field: 'duration_dung_hang',
      headerName: 'Thời gian đúng hạng',
      valueFormatter: (params) =>
        params.value ? Math.floor(params.value * 100) / 100 : 0,
    },
    { field: 'success_ratio', headerName: 'Tỷ lệ nhận diện' },
    { field: 'ten_khoa_hoc', headerName: 'Tên khoá học' },
    { field: 'vehicle_hang', headerName: 'Hạng xe' },
    { field: 'vehicle_plate', headerName: 'Biển số xe' },
    { field: 'instructor_name', headerName: 'Tên giáo viên' },
  ], [allChecked, rowData]);

  const getDatData = async (q) => {
    setLoading(true);
    try {
      const data = await datApi.query(q);
      setRowData(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const searchDatData = async (q) => {
    setLoading(true);
    try {
      const data = await datApi.search(q);
      setRowData(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckAllChange = useCallback(
    (isChecked) => {
      const updatedRows = rowData.map((row) => ({
        ...row,
        checked: isChecked,
      }));
      setRowData(updatedRows);

      updatedRows.forEach((row) => {
        const body = {
          session_id: row.session_id,
          checked: isChecked,
          violation: row.violation,
          excel_sync: row.excel_sync,
          manual_night_duration: row.manual_night_duration,
        };
        datApi
          .update(body)
          .then(() => {
            toast(
              `${isChecked ? 'Chọn' : 'Bỏ chọn'} thành công phiên ${
                row.session_id
              } cho ${row.trainee_name}`,
              { type: 'success' }
            );
          })
          .catch(() => {
            toast('Cập nhật thất bại', { type: 'error' });
          });
      });
    },
    [rowData]
  );

  const handleApplyCheckAll = () => {
    if (rowData.length > 20) {
      toast('Chỉ có thể chọn/bỏ chọn tất cả tối đa 20 phiên.', { type: 'warning' });
      setShowCheckAllModal(false);
      return;
    }
    const isChecked = checkAllAction === 'check';
    handleCheckAllChange(isChecked);
    setShowCheckAllModal(false);
    setSelectAllChecked(false);
  };

  const SYNC_CHUNK_DAYS = 4;

  const addDaysToDateStr = (dateStr, days) => {
    const d = new Date(`${dateStr}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  };

  const handleSyncBtn = async () => {
    if (!startDate || !endDate) {
      return toast('Vui lòng chọn thời gian', { type: 'warning' });
    }

    if (!provider) {
      return toast('Vui lòng chọn nhà cung cấp', { type: 'warning' });
    }

    const _startDate = new Date(`${startDate}T00:00:00Z`).getTime();
    const _endDate = new Date(`${endDate}T00:00:00Z`).getTime();

    if (_startDate > _endDate) {
      return toast('Ngày bắt đầu không thể lớn hơn ngày kết thúc', {
        type: 'warning',
      });
    }

    if (_endDate - _startDate > 16 * 24 * 60 * 60 * 1000) {
      return toast('Khoảng thời gian tối đa là 16 ngày', { type: 'warning' });
    }

    const totalDays =
      Math.round((_endDate - _startDate) / (24 * 60 * 60 * 1000)) + 1;
    const chunks = [];
    for (let i = 0; i < totalDays; i += SYNC_CHUNK_DAYS) {
      const chunkStart = addDaysToDateStr(startDate, i);
      const chunkEnd = addDaysToDateStr(
        startDate,
        Math.min(i + SYNC_CHUNK_DAYS - 1, totalDays - 1)
      );
      chunks.push({ start: chunkStart, end: chunkEnd });
    }

    setSyncing(true);

    let totalCount = 0;
    const failedChunks = [];
    try {
      for (let i = 0; i < chunks.length; i++) {
        const { start, end } = chunks[i];
        try {
          const res = await datApi.sync(provider, start, end);
          totalCount += res?.count || 0;
        } catch (err) {
          failedChunks.push(`${start} → ${end}`);
        }
      }

      if (failedChunks.length === 0) {
        toast(
          `Đã đồng bộ ${totalCount} phiên thành công (${chunks.length} đợt)`,
          { type: 'success' }
        );
        setShowModal(false);
      } else if (totalCount > 0) {
        toast(
          `Đã đồng bộ ${totalCount} phiên, ${failedChunks.length}/${chunks.length} đợt lỗi: ${failedChunks.join('; ')}`,
          { type: 'warning' }
        );
      } else {
        toast('Đồng bộ lỗi toàn bộ các đợt, vui lòng thử lại', {
          type: 'error',
        });
      }
    } finally {
      setSyncing(false);
      getDatData({ page });
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();

    if (!name) {
      alert('Vui lòng nhập tên/mã phiên/mã học viên cần tra cứu');
      return;
    }

    debouncedSearch({
      search: {
        ...(name && { name }),
        ...(drivingClass && { drivingClass }),
      },
      filter: {
        ...(violation && { violation: { $regex: violation === 'Phiên học trùng lặp giáo viên' ? 'Trùng' : violation, $options: "i" } }),
        ...(!checked && { checked: false }),
      },
      skip: (page - 1) * 50,
      limit: 50,
    });
  };

  const debouncedSearch = useMemo(() => debounce(searchDatData, 1000), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    debouncedSearch({
      search: {
        ...(name && { name }),
        ...(drivingClass && { drivingClass }),
      },
      filter: {
        ...(violation && { violation: { $regex: violation === 'Phiên học trùng lặp giáo viên' ? 'Trùng' : violation, $options: "i" } }),
        ...(!checked && { checked: false }),
      },
      skip: (page - 1) * 50,
      limit: 50,
    });
  }, [page, checked, violation, drivingClass, name]);

  const onBtnExportCsv = () => {
    gridRef.current.api.exportDataAsCsv();
  };

  const onCellValueChanged = (params) => {
    const { data } = params;
    const body = {
      session_id: data.session_id,
      checked: data.checked,
      violation: data.violation,
      excel_sync: data.excel_sync,
      manual_night_duration: data.manual_night_duration,
    };

    datApi
      .update(body)
      .then(() => {
        toast(`Cập nhật thành công phiên ${data.session_id} cho ${data.trainee_name}`, { type: 'success' });
      })
      .catch(() => {
        toast('Cập nhật thất bại', { type: 'error' });
      });
  };

  const onBtnCheckError = () => {
    setLoading(true);
    datApi.checkError().then((res) => {
      toast('Kiểm lỗi thành công', { type: 'success' });
    }).catch((err) => {
      toast('Kiểm lỗi thất bại', { type: 'error' });
    }).finally(() => {
      setLoading(false);
    });
  }

  useEffect(() => {
    setSelectAllChecked(false);
  }, [rowData]);

  useEffect(() => {
      const totalDuration = rowData.reduce(
        (acc, cur) => acc + (!cur.violation?.length ? cur.duration : 0),
        0
      );
      const totalDistance = rowData.reduce(
        (acc, cur) => acc + (!cur.violation?.length ? cur.distance : 0),
        0
      );
      const totalDurationDungHang = rowData.reduce(
        (acc, cur) => acc + (!cur.violation?.length ? cur.duration_dung_hang : 0),
        0
      );
  
      const totalDistanceDungHang = rowData.reduce(
        (acc, cur) => acc + (!cur.violation?.length ? cur.distance_dung_hang : 0),
        0
      );
  
      const totalNightDurationDungHang = rowData.reduce((acc, cur) => {
        let val = 0;
        if (
          cur.ten_khoa_hoc?.includes('B1') ||
          cur.ten_khoa_hoc?.includes('BTĐ')
        ) {
          val = ['B1', 'B.01', 'B11'].includes(cur.vehicle_hang)
            ? cur.night_duration
              ? cur.night_duration
              : cur.manual_night_duration
              ? cur.manual_night_duration
              : 0
            : 0;
        } else if (
          cur.ten_khoa_hoc?.includes('B2') ||
          cur.ten_khoa_hoc?.includes('BSS')
        ) {
          val = ['B2', 'B'].includes(cur.vehicle_hang)
            ? cur.night_duration
              ? cur.night_duration
              : cur.manual_night_duration
              ? cur.manual_night_duration
              : 0
            : 0;
        } else if (cur.ten_khoa_hoc?.includes('C1')) {
          val = ['C1', 'C'].includes(cur.vehicle_hang)
            ? cur.night_duration
              ? cur.night_duration
              : cur.manual_night_duration
              ? cur.manual_night_duration
              : 0
            : 0;
        }
        return acc + (!cur.violation?.length ? parseFloat(val) : 0);
      }, 0);

      const totalNightDistance = rowData.reduce(
        (acc, cur) => acc + (!cur.violation?.length ? cur.night_distance : 0),
        0
      );
  
      const totalAutoDuration = rowData.reduce(
        (acc, cur) => acc + (!cur.violation?.length ? cur.auto_duration : 0),
        0
      );
  
      const totalAutoDistance = rowData.reduce(
        (acc, cur) => acc + (!cur.violation?.length ? cur.auto_distance : 0),
        0
      );
  
      const manualNightDuration = rowData.reduce(
        (acc, cur) =>
          acc +
          (!cur.violation?.length && cur.manual_night_duration
            ? parseFloat(cur.manual_night_duration)
            : 0),
        0
      );
  
      const result = checkDATConditions(
        rowData[0]?.ten_khoa_hoc?.split('-')?.[0],
        totalDuration,
        totalDistance,
        totalDurationDungHang,
        totalNightDurationDungHang,
        totalAutoDuration
      );
      setResult({
        totalDuration,
        totalDistance,
        totalDurationDungHang,
        totalDistanceDungHang,
        totalNightDurationDungHang,
        totalNightDistance,
        totalAutoDuration,
        totalAutoDistance,
        manualNightDuration,
        ...result,
      });
    }, [rowData]);

  return (
    <Container
      style={{
        height: '100vh',
      }}
      fluid
    >
      <Row
        className='d-flex justify-content-center align-items-center'
        style={{ height: '10%' }}
      >
        <Col xs={3}>
          <Form.Control
            type='text'
            placeholder='Nhập tên/mã phiên/mã học viên cần tra cứu'
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Col>
        <Col>
          <Form.Control
            type='text'
            placeholder='Mã khoá học (không bắt buộc)'
            value={drivingClass}
            onChange={(e) => setDrivingClass(e.target.value)}
          />
        </Col>
        <Col>
          <Form.Check
            label='Phiên chưa kiểm tra'
            checked={!checked}
            type='checkbox'
            onChange={(e) => {
              setChecked(!e.target.checked);
            }}
          />
        </Col>
        <Col>
          <Form.Control
            as='select'
            value={violation}
            onChange={(e) => {
              setPage(1);
              setViolation(e.target.value);
            }}
            disabled={syncing}
          >
            <option value=''>Lọc theo</option>
            {VIOLATION_LIST.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Form.Control>
        </Col>
        <Col>
          <Button onClick={handleSearch} variant='primary' disabled={loading}>
            Tra cứu
          </Button>
        </Col>
        {Object.values(PROVIDERS).map((p) => (
          <Col key={p}>
            <Button
              onClick={() => {
                setProvider(p);
                setShowModal(true);
              }}
              variant='primary'
            >
              Đồng bộ {p}
            </Button>
          </Col>
        ))}

        <Col>
          <Button variant='primary' onClick={onBtnExportCsv}>
            Xuất Excel
          </Button>
        </Col>

        <Col>
          <Button
            variant='success'
            onClick={() => {
              if (!rowData.length) {
                toast('Không có dữ liệu để xuất báo cáo', { type: 'warning' });
                return;
              }
              setShowReportModal(true);
            }}
          >
            Xuất báo cáo
          </Button>
        </Col>

        <Col>
          <Button variant='primary' onClick={onBtnCheckError}>
            Kiểm lỗi
          </Button>
        </Col>

        <Col xs='auto'>
          <AdminLogoutButton />
        </Col>
      </Row>
      <div
        className='d-flex justify-content-around align-items-start p-2 flex-wrap'
        style={{ height: '10%' }}
      >
        <p>
          Tổng TG:{' '}
          <span className='fw-bold'>
            {convertHoursToHM(result?.totalDuration)}
            <br />(
            {result?.details?.totalTG === 'Đã đủ' ? (
              <span className='text-success'>{result?.details?.totalTG}</span>
            ) : (
              <span className='text-danger'>{result?.details?.totalTG}</span>
            )}
            )
          </span>
        </p>
        <p>
          Tổng QĐ:{' '}
          <span className='fw-bold'>
            {Math.floor(result?.totalDistance * 100) / 100} km
            <br />(
            {result?.details?.totalQD === 'Đã đủ' ? (
              <span className='text-success'>{result?.details?.totalQD}</span>
            ) : (
              <span className='text-danger'>{result?.details?.totalQD}</span>
            )}
            )
          </span>
        </p>
        <p>
          TG đúng hạng:{' '}
          <span className='fw-bold'>
            {convertHoursToHM(result?.totalDurationDungHang)} <br />(
            {result?.details?.TG_dung_hang === 'Đã đủ' ? (
              <span className='text-success'>
                {result?.details?.TG_dung_hang}
              </span>
            ) : (
              <span className='text-danger'>
                {result?.details?.TG_dung_hang}
              </span>
            )}
            )
          </span>
        </p>
        <p>
          QĐ đúng hạng: {Math.floor(result?.totalDistanceDungHang * 100) / 100}{' '}
          km
        </p>
        {/* <p>
          TG ban đêm:{' '}
          <span className='fw-bold'>
            {convertHoursToHM(
              result?.totalNightDuration + result?.manualNightDuration
            )}
            <br />(
            {result?.details?.TG_ban_dem === 'Đã đủ' ? (
              <span className='text-success'>
                {result?.details?.TG_ban_dem}
              </span>
            ) : (
              <span className='text-danger'>{result?.details?.TG_ban_dem}</span>
            )}
            )
          </span>
        </p>
        <p>
          QĐ ban đêm: {Math.floor(result?.totalNightDistance * 100) / 100} km
        </p> */}
        <p>
          TG đêm đúng hạng:{' '}
          <span className='fw-bold'>
            {convertHoursToHM(
              result?.totalNightDurationDungHang
            )}
            <br />
            {result?.details?.TG_ban_dem_dung_hang === 'Đã đủ' ? (
              <span className='text-success'>
                {result?.details?.TG_ban_dem_dung_hang}
              </span>
            ) : (
              <span className='text-danger'>{result?.details?.TG_ban_dem_dung_hang}</span>
            )}
          </span>
          {result?.manualNightDuration ? (
            <div>
              (TG nhập tay:{' '}
              {convertHoursToHM(result?.manualNightDuration)})
            </div>
          ) : null}
        </p>
        <p>
          TG tự động:{' '}
          <span className='fw-bold'>
            {convertHoursToHM(result?.totalAutoDuration)}
            <br />(
            {result?.details?.TG_tu_dong === 'Đã đủ' ? (
              <span className='text-success'>
                {result?.details?.TG_tu_dong}
              </span>
            ) : (
              <span className='text-danger'>{result?.details?.TG_tu_dong}</span>
            )}
            )
          </span>
        </p>
        <p>
          QĐ tự động:{' '}
          <span className={result?.totalAutoDistance > 120 ? 'text-danger fw-bold' : ''}>
            {Math.floor(result?.totalAutoDistance * 100) / 100} km
          </span>
        </p>
      </div>
      <Row className='ag-theme-quartz' style={{ height: '72%' }}>
        {loading && <p className='text-center'>Đang tải dữ liệu...</p>}
        {rowData.length && !loading && (
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={colDefs}
            onCellValueChanged={onCellValueChanged}
            gridOptions={{
              getRowStyle: (params) => {
                if (params.data?.violation?.length) {
                  return { backgroundColor: '#FF6666' };
                }
                return null;
              },
            }}
          />
        )}
        {!loading && !rowData.length && (
          <p className='text-center'>Không có dữ liệu</p>
        )}
      </Row>
      <div
        className='d-flex justify-content-center align-items-center'
        style={{ height: '8%' }}
      >
        <Pagination className='mb-0'>
          <Pagination.First onClick={() => setPage(1)} />
          <Pagination.Prev
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          />
          {page > 1 && (
            <Pagination.Item onClick={() => setPage(page - 1)}>
              {page - 1}
            </Pagination.Item>
          )}
          <Pagination.Item active>{page}</Pagination.Item>
          <Pagination.Item onClick={() => setPage(page + 1)}>
            {page + 1}
          </Pagination.Item>
          <Pagination.Next
            onClick={() => setPage(page + 1)}
            disabled={rowData.length < 20}
          />
        </Pagination>
      </div>
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Đồng bộ {provider}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label>Từ ngày</Form.Label>
          <Form.Control
            type='date'
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Form.Label className='mt-3'>Đến ngày (bao gồm)</Form.Label>
          <Form.Control
            type='date'
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowModal(false)}>
            Đóng
          </Button>
          <Button variant='primary' onClick={handleSyncBtn} disabled={syncing}>
            {syncing ? 'Đang đồng bộ...' : 'Bắt đầu đồng bộ'}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showCheckAllModal} onHide={() => setShowCheckAllModal(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            {checkAllAction === 'check' ? 'Chọn tất cả' : 'Bỏ chọn tất cả'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='mb-3'>
            {checkAllAction === 'check'
              ? `Sẽ chọn tất cả ${rowData.length} phiên sau:`
              : `Sẽ bỏ chọn tất cả ${rowData.length} phiên sau:`}
          </p>
          <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px', borderRadius: '4px' }}>
            <table style={{ width: '100%', fontSize: '14px' }}>
              <thead>
                <tr>
                  <th style={{ borderBottom: '1px solid #ddd', paddingBottom: '8px', paddingRight: '8px', textAlign: 'left' }}>STT</th>
                  <th style={{ borderBottom: '1px solid #ddd', paddingBottom: '8px', paddingRight: '8px', textAlign: 'left' }}>Mã học viên</th>
                  <th style={{ borderBottom: '1px solid #ddd', paddingBottom: '8px', paddingRight: '8px', textAlign: 'left' }}>Tên học viên</th>
                  <th style={{ borderBottom: '1px solid #ddd', paddingBottom: '8px', paddingRight: '8px', textAlign: 'left' }}>Mã phiên</th>
                </tr>
              </thead>
              <tbody>
                {rowData.map((row, index) => (
                  <tr key={row.session_id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ paddingTop: '8px', paddingBottom: '8px', paddingRight: '8px' }}>{index + 1}</td>
                    <td style={{ paddingTop: '8px', paddingBottom: '8px', paddingRight: '8px' }}>{row.trainee_ma_dk}</td>
                    <td style={{ paddingTop: '8px', paddingBottom: '8px', paddingRight: '8px' }}>{row.trainee_name}</td>
                    <td style={{ paddingTop: '8px', paddingBottom: '8px', paddingRight: '8px' }}>{row.session_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowCheckAllModal(false)}>
            Huỷ
          </Button>
          <Button
            variant={checkAllAction === 'check' ? 'success' : 'warning'}
            onClick={handleApplyCheckAll}
          >
            {checkAllAction === 'check' ? 'Chọn tất cả' : 'Bỏ chọn tất cả'}
          </Button>
        </Modal.Footer>
      </Modal>
      <SessionReportExportModal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        sessions={rowData}
      />
    </Container>
  );
}

export default AdminPage;
